import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Check } from 'lucide-react';
import { api, uploadAsset } from '../api/client';
import { useStudio } from './App';
import { Spinner, useApi } from './shared';
import type { Asset } from './production';

export type CharacterReference = {
  characterId: string;
  name: string;
  revision: string;
  hash: string;
  asset: Asset | null;
};
export type ReferenceCatalog = {
  items: CharacterReference[];
  maxImages: number;
  maxImageBytes: number;
  maxTotalBytes: number;
};
export function useCharacterReferences(seriesId?: string, branchId?: string) {
  const { base } = useStudio();
  return useApi<ReferenceCatalog>(
    `${base}/series/${seriesId}/reference-images?branchId=${branchId}`,
    !!seriesId && !!branchId,
  );
}
export function castForShot(
  roles: CharacterReference[],
  shot?: { characterIds?: string[]; description: string; speaker: string },
) {
  if (!shot) return [];
  return (
    shot.characterIds ??
    roles
      .filter((r) => r.name && (shot.speaker === r.name || shot.description.includes(r.name)))
      .map((r) => r.characterId)
  );
}
export function ReferenceSelection({
  roles,
  ids,
  onChange,
  limit = 7,
}: {
  roles: CharacterReference[];
  ids: string[];
  onChange: (ids: string[]) => void;
  limit?: number;
}) {
  const selected = ids.map((id) => roles.find((r) => r.characterId === id)).filter((r) => !!r);
  const missing = selected.filter((r) => !r.asset);
  return (
    <div className="reference-selection">
      <strong>
        出场角色参考图 · {selected.filter((r) => r.asset).length} / {limit} 张
      </strong>
      <div className="reference-cast">
        {roles.map((role) => (
          <label key={role.characterId}>
            <input
              type="checkbox"
              checked={ids.includes(role.characterId)}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? [...ids, role.characterId]
                    : ids.filter((id) => id !== role.characterId),
                )
              }
            />
            {role.name}
          </label>
        ))}
        {!roles.length && (
          <span className="quiet">还没有角色，可先到角色库建立角色并确认定妆图。</span>
        )}
      </div>
      {!!selected.length && (
        <div className="reference-thumbnails">
          {selected.map((role, i) => (
            <figure key={role.characterId}>
              {role.asset ? (
                <img src={role.asset.url} alt={`${role.name}的已确认参考图`} />
              ) : (
                <span className="reference-empty">待确认图片</span>
              )}
              <figcaption>
                {i + 1}. {role.name}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {ids.length > limit && (
        <p className="form-error">最多 {limit} 张。请减少出场角色或拆分镜头，不会自动丢图。</p>
      )}
      {!!missing.length && (
        <p className="form-error">
          请在角色库确认参考图：{missing.map((r) => r.name).join('、')}。
        </p>
      )}
      <small>每位角色使用一张已确认的定妆图。只勾选画面中出场的人物，画外音不用勾选。</small>
    </div>
  );
}
export function ConfirmReferenceButton({
  characterId,
  asset,
}: {
  characterId: string;
  asset: Asset;
}) {
  const { base, project, branch } = useStudio();
  const refs = useCharacterReferences(project?.id, branch?.id);
  const current = refs.data?.items.find((r) => r.characterId === characterId);
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const confirmed = current?.asset?.id === asset.id;
  return (
    <div className="reference-confirm">
      <button
        type="button"
        className="button secondary"
        disabled={busy || !current || confirmed}
        onClick={() => {
          setBusy(true);
          setError('');
          void api(
            `${base}/series/${project?.id}/characters/${characterId}/reference-image?branchId=${branch?.id}`,
            'PUT',
            { expectedRevision: current!.revision, assetId: asset.id, humanReviewed: true },
          )
            .then(() =>
              cache.invalidateQueries({
                queryKey: [
                  'api',
                  `${base}/series/${project?.id}/reference-images?branchId=${branch?.id}`,
                ],
              }),
            )
            .catch((e: Error) => setError(e.message))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? <Spinner /> : <Check size={16} />}{' '}
        {confirmed ? '已确认为视频参考图' : '确认用作此角色的视频参考图'}
      </button>
      {(error || refs.error) && <p className="form-error">{error || refs.error?.message}</p>}
    </div>
  );
}
export function CharacterReferencePanel({ characterId }: { characterId: string }) {
  const { base, project, branch } = useStudio();
  const refs = useCharacterReferences(project?.id, branch?.id);
  const current = refs.data?.items.find((r) => r.characterId === characterId);
  const [uploaded, setUploaded] = useState<Asset | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <section className="character-reference-panel" aria-label="视频参考图">
      <strong>视频参考图</strong>
      <p className="quiet">
        确认定妆图后，包含此角色的新视频会携带这张图片。生成新候选不会自动替换它。
      </p>
      {current?.asset ? (
        <figure className="reference-current">
          <img src={current.asset.url} alt={`${current.name}的当前视频参考图`} />
          <figcaption>已确认 · 第 {current.revision} 版</figcaption>
        </figure>
      ) : (
        <p className="quiet">尚未确认。请从上方已保存的图片中选择，或上传图片后确认。</p>
      )}
      <label className="button secondary">
        <ImagePlus size={16} />
        {busy ? '正在上传…' : '上传参考图候选'}
        <input
          type="file"
          hidden
          accept="image/png,image/jpeg,image/webp"
          disabled={busy || !project}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            if (file.size > 8 * 1024 * 1024) {
              setError('参考图每张不超过 8 MB');
              return;
            }
            setBusy(true);
            setError('');
            void uploadAsset(`${base}/assets?seriesId=${project!.id}`, file)
              .then((a) => setUploaded(a as Asset))
              .catch((err: Error) => setError(err.message))
              .finally(() => setBusy(false));
          }}
        />
      </label>
      {uploaded && (
        <div className="reference-uploaded">
          <img src={uploaded.url} alt="刚上传的参考图候选" />
          <ConfirmReferenceButton characterId={characterId} asset={uploaded} />
        </div>
      )}
      {(error || refs.error) && <p className="form-error">{error || refs.error?.message}</p>}
      <small>PNG、JPEG、WebP，每张最多 8 MB；每个视频最多 7 张、合计最多 32 MB。</small>
    </section>
  );
}
